/** Keyboard and touch controls preserve held actions and queued ejection edges. */
import type { SeededRandom } from './rng.js';
import { EMPTY_INPUT, type Controller, type PlaneInput, type SimState } from './sim.js';
const KEY_ACTION: Readonly<Record<string, keyof PlaneInput>> = {
  arrowup: 'climb',
  w: 'climb',
  '2': 'climb',
  arrowdown: 'dive',
  s: 'dive',
  '8': 'dive',
  arrowleft: 'left',
  a: 'left',
  '4': 'left',
  arrowright: 'right',
  d: 'right',
  '6': 'right',
  ' ': 'fire',
  z: 'fire',
  '5': 'fire',
  e: 'eject',
  '#': 'eject',
};
export class KeyboardController implements Controller {
  private readonly keys = new Set<string>();
  private ejectQueued = false;
  private readonly down = (e: KeyboardEvent): void => {
    if (KEY_ACTION[e.key.toLowerCase()]) {
      e.preventDefault();
      this.setKey(e.key, true);
    }
  };
  private readonly up = (e: KeyboardEvent): void => {
    this.setKey(e.key, false);
  };
  private readonly blur = (): void => this.clear();
  attach(): void {
    this.clear();
    window.addEventListener('keydown', this.down);
    window.addEventListener('keyup', this.up);
    window.addEventListener('blur', this.blur);
  }
  detach(): void {
    window.removeEventListener('keydown', this.down);
    window.removeEventListener('keyup', this.up);
    window.removeEventListener('blur', this.blur);
    this.clear();
  }
  clear(): void {
    this.keys.clear();
    this.ejectQueued = false;
  }
  setKey(key: string, held: boolean): void {
    const normalized = key.toLowerCase();
    if (held) {
      if (!this.keys.has(normalized) && KEY_ACTION[normalized] === 'eject') this.ejectQueued = true;
      this.keys.add(normalized);
    } else this.keys.delete(normalized);
  }
  poll(_state: SimState, _side: 0 | 1, _rng: SeededRandom): PlaneInput {
    const input = { ...EMPTY_INPUT };
    for (const key of this.keys) {
      const action = KEY_ACTION[key];
      if (action && action !== 'eject') input[action] = true;
    }
    input.eject = this.ejectQueued;
    this.ejectQueued = false;
    return input;
  }
}
export class TouchController implements Controller {
  private readonly pointers = new Map<number, number>();
  private ejectQueued = false;
  private readonly down = (e: PointerEvent): void => {
    const canvas = document.getElementById('screen');
    if (!(canvas instanceof HTMLCanvasElement) || e.target !== canvas) return;
    e.preventDefault();
    const r = canvas.getBoundingClientRect();
    this.setPointer(
      e.pointerId,
      TouchController.zoneAt(e.clientX - r.left, e.clientY - r.top, r.width, r.height),
    );
    canvas.setPointerCapture(e.pointerId);
  };
  private readonly move = (e: PointerEvent): void => {
    if (!this.pointers.has(e.pointerId)) return;
    const canvas = document.getElementById('screen');
    if (!(canvas instanceof HTMLCanvasElement)) return;
    const r = canvas.getBoundingClientRect();
    this.setPointer(
      e.pointerId,
      TouchController.zoneAt(e.clientX - r.left, e.clientY - r.top, r.width, r.height),
    );
  };
  private readonly up = (e: PointerEvent): void => this.releasePointer(e.pointerId);
  private readonly blur = (): void => this.clear();
  static zoneAt(x: number, y: number, w: number, h: number): number {
    if (y > h * 0.72) return x < w / 2 ? 2 : 3;
    if (y < h * 0.28) return x < w * 0.75 ? 4 : 5;
    return x < w / 2 ? 0 : 1;
  }
  setPointer(id: number, zone: number): void {
    if (zone === 5 && this.pointers.get(id) !== 5) this.ejectQueued = true;
    this.pointers.set(id, zone);
  }
  releasePointer(id: number): void {
    this.pointers.delete(id);
  }
  clear(): void {
    this.pointers.clear();
    this.ejectQueued = false;
  }
  attach(): void {
    this.clear();
    window.addEventListener('pointerdown', this.down);
    window.addEventListener('pointermove', this.move);
    window.addEventListener('pointerup', this.up);
    window.addEventListener('pointercancel', this.up);
    window.addEventListener('blur', this.blur);
  }
  detach(): void {
    window.removeEventListener('pointerdown', this.down);
    window.removeEventListener('pointermove', this.move);
    window.removeEventListener('pointerup', this.up);
    window.removeEventListener('pointercancel', this.up);
    window.removeEventListener('blur', this.blur);
    this.clear();
  }
  poll(_state: SimState, _side: 0 | 1, _rng: SeededRandom): PlaneInput {
    const input = { ...EMPTY_INPUT };
    for (const zone of this.pointers.values()) {
      const action = (['left', 'right', 'climb', 'dive', 'fire'] as const)[zone];
      if (action) input[action] = true;
    }
    input.eject = this.ejectQueued;
    this.ejectQueued = false;
    return input;
  }
}
export class HumanController implements Controller {
  constructor(private readonly parts: Controller[]) {}
  poll(state: SimState, side: 0 | 1, rng: SeededRandom): PlaneInput {
    const merged = { ...EMPTY_INPUT };
    for (const part of this.parts) {
      const input = part.poll(state, side, rng);
      for (const action of Object.keys(input) as (keyof PlaneInput)[])
        if (input[action]) merged[action] = true;
    }
    return merged;
  }
}
